import { defineComponent as _defineComponent } from 'vue'
import { ActionProps } from '@/components/atoms/action/Action.vue';
import Loading from '@/components/atoms/loading/Loading.vue';
import FormContainer from '@/components/molecules/form/FormContainer.vue';
import { CheckboxInputData, DateInputData, NumberInputData, TextInputData } from '@/components/molecules/input/types';
import PageHeader from '@/components/molecules/page/header/PageHeader.vue';
import GoogleMapsService from '@/services/maps/GoogleMapsService';
import { formatNumberToMoneyString, parseMoneyToNumber } from '@/utils/formatters';
import { computed, getCurrentInstance, onMounted, reactive } from 'vue';
import RequestPreview from './preview/RequestPreview.vue';
import NewRequestStepper from '../common/stepper/NewRequestStepper.vue';
import getFieldsConfig from './branch';
import NewRequestDataFormKeys from './branch/keys';
import NewRequestDataConfig from './branch/types';
import { convertDateFormat, getInterval } from './utils';

type NewRequestDataState = {
  isLoading: boolean;
  isLoadingValidation: boolean;
  config: NewRequestDataConfig;
  validatedData: any;
  currentBranch: any;
  coinsurance_companies: any;
  subscriptor: any;
  conditions: any;
  branch: any;
}

type NewRequestDataForm = {
  insured_fullname: TextInputData;
  activity: TextInputData;
  validity_from: DateInputData;
  validity_to: DateInputData;
  aon_sum_extra: TextInputData;
  original_tax: TextInputData;
  original_gross_bonus: TextInputData;
  original_gross_bonus_partcial: TextInputData;
  original_gross_aon: TextInputData;
  notes: TextInputData;
  coordinate_lat: NumberInputData;
  coordinate_lng: NumberInputData;
  address: TextInputData;
  country: TextInputData;
  province: TextInputData;
  city: TextInputData;
  zipcode: TextInputData;
  building: TextInputData;
  business_interruption: TextInputData;
  content: TextInputData;
  total_sum_secured: TextInputData;
  floors: NumberInputData,
  yearBuilt: NumberInputData,
  beachFront: NumberInputData,
  coinsurance_participation: CheckboxInputData,
  excluded_from_contract: CheckboxInputData,
  special_aceptation: CheckboxInputData,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'NewRequestData',
  setup(__props) {

const instance = getCurrentInstance();
const proxy = instance && instance.proxy as any;

const maps = new GoogleMapsService();

const title = 'Crear solicitud';

const state = reactive<NewRequestDataState>({
  isLoading: false,
  isLoadingValidation: false,
  config: null,
  validatedData: null,
  currentBranch: proxy.$store.state.newRequest.branch,
  coinsurance_companies: [],
  subscriptor: {
    value: proxy.$store.state.user.fullname
  },
  conditions: {},
  branch: {},
});

const form = reactive({
  insured_fullname: {
    label: "Nombre de los Asegurados",
    maxLength: 120,
    value: "",
    required: true,
    start: 1,
    end: 7,
    col: 6
  },
  activity: {
    label: "Actividad",
    maxLength: 120,
    value: "CONDOMINIOS",
    readonly: true,
    start: 7,
    end: 13,
    col: 6
  },
  validity_from: {
    label: "Vigencia Desde",
    value: "",
  },
  validity_to: {
    label: "Vigencia Hasta",
    value: "",
  },
  aon_sum_extra: {
    value: "0,00",
    label: "Suma Extra Cedida a AON",
    isMoney: true,
  },
  original_tax: {
    value: "0,00",
    label: "Tasa Original",
  },
  original_gross_bonus: {
    value: "0,00",
    label: "Prima Bruta Original Anual",
    isMoney: true,
  },
  original_gross_bonus_parcial: {
    value: "0,00",
    label: "Prima Bruta Original por el periodo",
    isMoney: true,
  },
  original_gross_aon: {
    value: "99,99",
    label: "Prima Bruta Cedida a AON",
    isMoney: true,
  },
  notes: {
    value: "",
    label: "Observaciones",
  },
  coordinate_lat: {
    value: 0,
  },
  coordinate_lng: {
    value: 0,
  },
  address: {
    type: "map",
    label: "Dirección",
    maxLength: 120,
    value: "",
    required: true,
    placeholder: "Ingrese una dirección",
    col: 6,
    id: maps.ID,
    onInputReady: (input: HTMLInputElement | null) => {
      maps.setAutocompleteInput(input);
      maps.initGoogleAutocomplete(fillInAddress);
    },
  },
  map: {
    type: "google-map",
    start: 7,
    end: 13,
    value: ""
  },
  country: {
    label: "País",
    value: "",
    required: true,
    readonly: true,
    col: 6,
  },
  province: {
    label: "Estado / Provincia",
    value: "",
    required: true,
    readonly: true,
    start: 1,
    end: 7,
  },
  city: {
    label: "Ciudad",
    value: "",
    required: true,
    readonly: true,
    start: 1,
    end: 7,
  },
  zipcode: {
    label: "Código postal",
    value: "",
    readonly: true,
    start: 1,
    end: 7,
  },
  building: {
    value: "",
    label: "Edificio",
    isMoney: true,
    start: 1,
    end: 4,
  },
  business_interruption: {
    value: "",
    label: "Interrupción de negocio",
    isMoney: true,
    start: 4,
    end: 7,
  },
  content: {
    value: "",
    label: "Contenido",
    isMoney: true,
    start: 7,
    end: 10,
  },
  total_sum_secured: {
    value: "",
    label: "Suma Total Asegurada",
    error: false,
    errorMessage: "",
    tooltip: "Suma de interrupción de negocio, edificio y contenido.",
    readonly: true,
    isMoney: true,
    col: 3
  },
  floors: {
    label: "Cantidad de pisos",
    value: 1,
    required: true,
    col: 4
  },
  yearBuilt: {
    label: "Año de construcción",
    value: 2020,
    min: 1900,
    max: 2026,
    required: true,
    col: 4
  },
  beachFront: {
    type: "checkbox",
    label: "Al frente de la playa",
    value: false,
    col: 4
  },
  coinsurance_participation: {
    value: "",
    label: "Participación en Coaseguro",
  },
  excluded_from_contract: {
    value: "",
    label: "Excluido del Contrato",
  },
  special_aceptation: {
    value: "",
    label: "Aceptación Especial",
  },
});

const formFields = computed(() => {
  let fieldsToReturn: any = {};
  if (!state.config || !state.config.fieldsToShow) return fieldsToReturn;

  state.config.fieldsToShow.forEach((field: string) => {
    if (form[field]) {
      fieldsToReturn[field] = form[field];
    }
  });
  return fieldsToReturn;
});

const moneyFields = computed<string[]>(() => Object.keys(form).filter(key => form[key].isMoney));

const fieldsWithErrors = () => {
  return Object.keys(form).filter((key) => (form[key].required && !Boolean(form[key].value)));
};

const isValidForm = reactive({ value: false });

const loadFieldsConfig = () => {
  state.config = getFieldsConfig(state.currentBranch.id);
}

const prefillForm = () => {
  if (state.conditions && Object.keys(state.conditions).length > 0) {
    Object.entries(state.conditions).forEach(
      ([key, value]) => {
        if (form[key]) {
          form[key] = { ...form[key], value };
        }
      }
    );
  }

  if (proxy.$store.state.newRequest.data) {
    Object.entries(proxy.$store.state.newRequest.data).forEach(
      ([key, value]) => {
        if (form[key]) {
          form[key] = { ...form[key], value };
        }
      }
    );
  }
};

const fillInAddress = () => {
  const mapData = maps.getMapData();
  if (!mapData) return;
  form.address.value = mapData.address;
  form.country.value = mapData.country;
  form.province.value = mapData.province;
  form.city.value = mapData.city;
  form.zipcode.value = mapData.zipcode;
  form.coordinate_lat.value = mapData.coordinate_lat;
  form.coordinate_lng.value = mapData.coordinate_lng;
};

const loadConditions = () => {
  Object.assign(state.conditions, proxy.$store.state.newRequest.conditions);
};

const calculateTotalSumSecured = () => {
  const fieldsToSum = [NewRequestDataFormKeys.BUSINESS_INTERRUPTION, NewRequestDataFormKeys.BUILDING, NewRequestDataFormKeys.CONTENT];
  const total = fieldsToSum.reduce((acc, field) => acc + parseMoneyToNumber(form[field].value as string), 0);
  form.total_sum_secured.value = formatNumberToMoneyString(total);
};

const parseMoneyFields = (obj: any) => {
  moneyFields.value.forEach((key) => {
    if (obj[key]) {
      const parsed = parseMoneyToNumber(obj[key]);
      obj[key] = isNaN(parsed) ? 0 : parsed;
    }
  });
};

const clearFieldErrors = () => {
  moneyFields.value.forEach((key) => {
    if (form[key]) {
      form[key].error = false;
      form[key].errorMessage = '';
    }
  });
};

const setFieldErrors = (errors: Array<{ field: string; message: string }>) => {
  errors.forEach((err) => {
    if (err.field && form[err.field]) {
      form[err.field].error = true;
      form[err.field].errorMessage = err.message || 'Campo inválido';
    }
  });
};

const handleBlur = async (key: string, value: string | number, calculate_tax_first?: any) => {
  isValidForm.value = fieldsWithErrors().length === 0;
  if (!moneyFields.value.includes(key)) return;

  calculateTotalSumSecured();
  clearFieldErrors();

  const requestData: any = getRequestData();
  const validationResponse: any = await validateForm(requestData);
  if (!validationResponse) return;

  if (validationResponse.status === false && validationResponse.data && Array.isArray(validationResponse.data.errors)) {
    setFieldErrors(validationResponse.data.errors);
    return;
  }
};

const getRequestData = () => ({
  rdCountry: form.country.value,
  branchId: state.currentBranch.id,
  insured_fullname: form.insured_fullname.value,
  city: form.city.value,
  province: form.province.value,
  address: form.address.value,
  activity: form.activity.value,
  coordinate_lat: form.coordinate_lat.value,
  coordinate_lng: form.coordinate_lng.value,
  total_sum_secured: form.total_sum_secured.value,
  coinsurance_participation: form.coinsurance_participation.value,
  excluded_from_contract: form.excluded_from_contract.value,
  content: form.content.value,
  special_aceptation: form.special_aceptation.value,
  building: form.building.value,
  business_interruption: form.business_interruption.value,
  original_gross_bonus: 0,
  original_gross_bonus_anual: 0,
  original_gross_aon: 0,
  original_tax: 0,
  aon_sum_extra: 0,
  original_gross_bonus_parcial: form.original_gross_bonus_parcial.value,
  notes: form.notes.value,
  companyId: proxy.$store.state.companyId,
  policy_number: state.conditions.policy_number,
  assignmentValidityAonFrom:state.conditions.validity_from,
  assignmentValidityAonTo: state.conditions.validity_to,
  validity_from: state.conditions.validity_from,
  validity_to: state.conditions.validity_to,
  assignment_percent_aon: 100,
  controlZoneId: state.conditions.controlZoneId,
  currencyId: state.conditions.currencyId,
  modifyType: 0,
});

const validateForm = async (requestData: any, calculate_tax_first?: boolean) => {
  const validator: any = {
    branchId: state.currentBranch.id,
    companyId: proxy.$store.state.companyId,
    currencyId: state.conditions.currencyId,
    building: requestData.building,
    original_gross_bonus: requestData.original_gross_bonus,
    original_gross_bonus_parcial: requestData.original_gross_bonus_parcial,
    original_tax: requestData.original_tax,
    content: requestData.content,
    aon_sum_extra: requestData.aon_sum_extra,
    business_interruption: requestData.business_interruption,
    total_sum_secured: requestData.total_sum_secured,
    yeardays: getInterval(state.conditions.validity_from, state.conditions.validity_to),
    period: getInterval(state.conditions.assignmentValidityAonFrom, state.conditions.assignmentValidityAonTo),
  };
  parseMoneyFields(validator);
  validator.calculate_tax_first = calculate_tax_first ? 1 : 0;

  try {
    state.isLoadingValidation = true;
    const response: any = await proxy.$root._post(
      'requests/validateform',
      validator
    );
    return response;
  } catch (e) {
    proxy.$root.notify(e, 'error');
    return false;
  } finally {
    state.isLoadingValidation = false;
  }
};

const buildRequest = () => {
  const validityFromFormatted = convertDateFormat(state.conditions.validity_from);
  const validityToFormatted = convertDateFormat(state.conditions.validity_to);
  const aonFromFormatted = convertDateFormat(state.conditions.validity_from);
  const aonToFormatted = convertDateFormat(state.conditions.validity_to);

  const requestData = state.validatedData;

  requestData.original_gross_bonus_anual = requestData.original_gross_bonus;
  requestData.original_gross_bonus = requestData.original_gross_bonus_parcial;
  requestData.countryId = proxy.$store.state.user.companies[0].countryId;
  requestData.controlZoneId =
    requestData.controlZoneId == 'NULL' ? null : requestData.controlZoneId;
  requestData.coinsurance_companies = state.coinsurance_companies.join(',');
  requestData.assignment_percent_aon = 100;
  requestData.original_gross_bonus = 0;
  requestData.original_gross_bonus_anual = 0;
  requestData.original_gross_aon = 0;
  requestData.original_tax = 0;
  requestData.aon_sum_extra = 0;
  requestData.validity_from = validityFromFormatted;
  requestData.validity_to = validityToFormatted;
  requestData.assignmentValidityAonFrom = aonFromFormatted;
  requestData.assignmentValidityAonTo = aonToFormatted;
  requestData.branchId = state.currentBranch.id;
  requestData.floors = form.floors.value;
  requestData.year_built = form.yearBuilt.value;
  requestData.beach_front = form.beachFront.value;
  requestData.total_sum_secured = requestData.building + requestData.business_interruption + requestData.content;

  return requestData;
};

const createRequest = async () => {
  if (state.validatedData && !state.isLoading) {
    try {
      state.isLoading = true;
      proxy.$modal.show("loading");
      const requestData = buildRequest();
      const res: any = await proxy.$root._post('requests', requestData);
      proxy.$modal.hide("loading");
      proxy.$root.notify('Solicitud creada con éxito!', 'success');
      setTimeout(() => clearNewRequest(res.id), 1000);
    } catch (e) {
      proxy.$modal.hide("loading");
      console.log(e);
      proxy.$root.notify(e, 'error');
    } finally {
      state.isLoading = false;
    }
  }
};

const cleanValidatedData = () => {
  state.validatedData = null;
};

const clearNewRequest = (code: string) => {
  proxy.$store.commit('clearNewRequest', false);
  goDetail(code);
};

const goDetail = (code: string) => {
  proxy.$router.push(`/requests/${code}/detail`);
};

const submitForm = async () => {
  if (!state.validatedData) {
    const requestData: any = getRequestData();
    const response: any = await validateForm(requestData);
    if (response) {
      state.validatedData = { ...requestData, ...response.data };
    }

    if (response.error) {
      //proxy.$root.notify(response.error, 'warn');
    }
  }
};

const prevStep = () => {
  let requestData = getRequestData();
  proxy.$store.state.newRequest.data = state.config.fieldsToShow.reduce((acc, key) => ({ ...acc, [key]: requestData[key] }), {})
  proxy.$router.push('/requests/new/conditions');
};

const cleanDataButton: ActionProps = {
  label: 'Cancelar',
  onClick: cleanValidatedData,
  variant: "danger"
};

const previewButton: ActionProps = {
  label: 'Previsualizar',
  onClick: submitForm,
  //disabled: !isValid.value,
  variant: "primary"
};

const submitButton: ActionProps = {
  label: 'Pedir cotización',
  onClick: createRequest,
  variant: "primary"
};

const getFormActions = (): ActionProps[] => [
  state.config.previewFields ? previewButton : null,
  !state.config.previewFields ? submitButton : null
].filter(Boolean);

const getPreviewActions = (): ActionProps[] => [
  submitButton,
  cleanDataButton
].filter(Boolean);

onMounted(async () => {
  loadFieldsConfig();
  loadConditions();
  prefillForm();
});

return { __sfc: true,instance, proxy, maps, title, state, form, formFields, moneyFields, fieldsWithErrors, isValidForm, loadFieldsConfig, prefillForm, fillInAddress, loadConditions, calculateTotalSumSecured, parseMoneyFields, clearFieldErrors, setFieldErrors, handleBlur, getRequestData, validateForm, buildRequest, createRequest, cleanValidatedData, clearNewRequest, goDetail, submitForm, prevStep, cleanDataButton, previewButton, submitButton, getFormActions, getPreviewActions, Loading, FormContainer, PageHeader, RequestPreview, NewRequestStepper }
}

})